import { downloadFile } from '@/common/utils'
import { excelPolling, IExcelDataRes } from '@/common/utils/polling'
import UploadExcel from '@/components/UploadExcel'
import { budgetImportExcel, fetchExcelAsyncResult } from '@/pages/rule/utils/api'
import { Button } from '@roo/roo'
import { useTranslation } from '@sailor/i18n-web'
import React, {  useEffect, useRef, useState } from 'react'
import type { FC } from 'react'
import styles from './index.module.less'
import DownloadLink, { DownloadTemplateTypeEnum } from '../DownloadLink'
import LoadingIcon from '@/components/LoadingIcon'
import UpdateLogDrawer from '../ExcelUpdateLog'

export interface  IExcelConfig{
  s3Name: string;
  url: string;
  fileName?: string;
}
export interface Props {
  value?: IExcelConfig;
  onChange?: (value: Props['value'] | undefined) => void;
  type?: 'update' | 'create';
  disabled?: boolean;
  rulePackId?: string;
  hideFormTip?: (hide: boolean) => void;
}

const ExcelInput: FC<Props> = (props) => {
  const { t } = useTranslation()
  const isUpdate = props.type === 'update'

  const [fileList, setFileList] = useState<{name:string, fileS3Url?: string}[]>(() => {
    if (props.value?.url) {
      return [{ name: props.value?.fileName || props.value?.s3Name, fileS3Url: props.value?.url }]
    } else {
      return []
    }
  })

  const excelTimeoutRef = useRef<number>()
  const [exportResult, setExportResult] = useState<{
    data?: IExcelDataRes,
    status: 'loading' | 'success' | 'error' | ''

  }>({ status: '', data: undefined })

  const remoteMessage = exportResult.data?.message

  useEffect(() => {
    if (props.hideFormTip) {
      const hasErrorMsg = Boolean(exportResult.status === 'error' && remoteMessage?.length)
      props.hideFormTip(hasErrorMsg || exportResult.status === 'loading')
    }

  }, [remoteMessage])


  async function handleUpload (file:Blob) {
    try {
      setExportResult({ status: 'loading', data: undefined })
      const urlToken = await budgetImportExcel({
        byteBuffer: file,
        fileName: file.name,
        importType: isUpdate ? '2' : '1',
        rulePackageId: isUpdate ? props.rulePackId : undefined,
      })
      excelPolling({
        requestFunction: async () => fetchExcelAsyncResult(urlToken),
        onTimeoutChange: (timeout) => {
          excelTimeoutRef.current = timeout
        },
        onFinish: (param) => {
          setExportResult({
            status: param.type === 'fail' ? 'error' : 'success',
            data: param.data,
          })
        },
      })
    } catch (error) {
      setExportResult({ status: 'error', data: { message: error.message || error.msg } })
    }
  }

  useEffect(() => () => { // 清除Excel导入定时器
    if (excelTimeoutRef.current) {
      clearTimeout(excelTimeoutRef.current)
    }
  }, [])

  function handleCancelCheck () {
    clearTimeout(excelTimeoutRef.current)
    setExportResult({ status: '', data: undefined })
    handleFileListChange([])
  }

  const currentFile = fileList[0]

  useEffect(() => {
    if (exportResult.status === 'error') { // 出错后,清空
      handleFileListChange([])
    } else if (exportResult.status === 'success') { // 成功后,回传url给form
      const { fileUrl = '', fileS3Name = '', fileName } = exportResult.data || {}
      props.onChange!({ s3Name: fileS3Name, url: fileUrl, fileName })

    }
  }, [exportResult.status])


  function handleFileListChange (newFileList:{name:string, fileS3Url?: string}[]) {
    setFileList(newFileList)
    if (newFileList.length === 0) {
      props.onChange!(undefined)
    }
  }


  return (
    <div style={{ width: '400px' }}>
      <UploadExcel onUpload={handleUpload} fileList={fileList} setFileList={handleFileListChange} disableDelete={exportResult.status === 'loading' || props.disabled} >
        <>
          <DownloadLink
            templateType={isUpdate ? DownloadTemplateTypeEnum.BALANCE_UPDATE_TEMP : DownloadTemplateTypeEnum.IMPORT_TEMP}
            rulePackId={props.rulePackId}
          >
            {t('staff_download_template', '下载模板')}
          </DownloadLink>
          {isUpdate && (<UpdateLogDrawer> <span >{t('e_pc_update_log', '更新记录')}</span> </UpdateLogDrawer>)}
        </>
      </UploadExcel>
      {currentFile && exportResult.status === 'loading' && (
        <div className={styles.statusContainer}>
          <div style={{ display: 'flex' }}>
            <LoadingIcon />
            <div  style={{ color: '#666666', paddingLeft: '10px' }}>{t('e_pc_validation_status', '数据校验中')}</div>
          </div>
          <Button type="text" onClick={handleCancelCheck}>
            <span style={{ color: '#FF5500' }}> {t('e_pc_cancel_action', '取消')} </span>
          </Button>
        </div>
      )}
      {currentFile && exportResult.status === 'success' && (
        <span style={{ color: '#00B600' }}  className={styles.statusContainer}>
          {remoteMessage}
        </span>
      )}
      {exportResult.status === 'error' &&  (
        <div  className={styles.statusContainer}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {remoteMessage && <span style={{ color: '#F21500', flex: 1 }}>{remoteMessage}</span>}
            {exportResult.data?.failFileUrl && (
              <>
                &ensp;&ensp;
                <Button onClick={() => downloadFile(exportResult.data?.failFileUrl as string)} type="text">
                  <div style={{ color: '#FF5500' }}>
                    {t('e_pc_error_report', '下载错误报告')}
                  </div>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


export default ExcelInput
